'use client'
import { memo, useRef, useState, type FC } from 'react'
import styled from 'styled-components'
import { defaultGrid, designGrid, innerGrid, mq, presets } from '@/styles'
import { motion, useInView } from 'motion/react'
import { fadeFromLeftVariants, listItemVariants, listVariants, sortBy } from '@/utils'
import type { TypeCmsContentList } from '@/schema'
import { getMediaPreview } from './ContentHeader'

type Props = {
  data: TypeCmsContentList['data']
}

type PropsItem = {
  data: TypeCmsContentList['data']['listItems'][0]
}


const Item:FC<PropsItem> = memo(({ data }) => {
  const [playVideo, setPlayVideo] = useState(true)

  return (
    <Li variants={listItemVariants} $playVideo={playVideo}>
      <div className='media-wrapper'>
        {getMediaPreview(data.medium, playVideo, setPlayVideo)}
      </div>
      <p>{data.caption}</p>
    </Li>
  )
})

const getItems = (data: Props['data']['listItems']) => data.map((item, idx) => {
  return (
    <Item key={idx} data={item} />
  )
})

export const ContentGrid:FC<Props> = memo(({ data }) => {
  const { title, subtitle, listItems } = data
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })
  const Items = getItems(listItems ? sortBy({ arr: listItems, keyname: 'id' }) : [])

  return (
    <Section>
      <div>
        <motion.hgroup ref={ref} variants={fadeFromLeftVariants} initial='initial' animate={isInView ? 'animate' : 'initial'}>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </motion.hgroup>
      </div>
      <Ul variants={listVariants} initial='initial' animate={isInView ? 'animate' : 'initial'}>
        {Items}
      </Ul>
    </Section>
  )
})

const Section = styled.section`
  ${designGrid({})}
  margin-bottom: 50px;
  padding-top: 50px;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 70px;
    padding-top: 70px;
  }

  > div {
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 5;
    }
  }

  hgroup {
    margin-bottom: 50px;
    text-align: left;

    ${mq.greaterThan('tablet')} {
      margin-bottom: 0;
      position: sticky;
      top: 100px;
    }

    h2,
    p {
      text-align: left;

      ${mq.greaterThan('tablet')} {
        text-align: left;
      }
    }

    p {
      ${presets.font50()}
    }
  }

  h2 {
    ${presets.font13_500()}
    margin-bottom: 20px;
    text-transform: uppercase;
  }
`

const Ul = styled(motion.ul)`
  grid-column: 1 / span 6;
  ${innerGrid(defaultGrid.mobile)}

  ${mq.greaterThan('tablet')} {
    grid-column: 6 / span 7;
  }
`


const Li = styled(motion.li)<{ $playVideo: boolean }>`
  grid-column: auto / span 3;
  margin-bottom: 30px;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 80px;
  }

  p {
    ${presets.font16_30()}
    text-align: center;
  }

  img,
  .placeholder {
    align-self: stretch;
    background-color: var(--color-black);
    border-radius: 20px;
    height: 165px;
    margin-bottom: 10px;

    ${mq.greaterThan('tablet')} {
      height: 300px;
      margin-bottom: 20px;
    }
  }

  img {
    &.cover {
      opacity: ${({ $playVideo }) => $playVideo ? 0 : 1 };
      position: absolute;
      height: 100%;
      object-fit: cover;
      transition: 300ms opacity linear;
      width: 100%;
    }
  }

  .placeholder {
    opacity: .2;
  }
`
